import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { PasswordInput } from '@/components/forms/password-input';
import { Form } from '@/components/ui/form';

import { useUpdateTeamPassword } from '../../api/updateTeamPassword';

import { SettingsFormWrapper, SettingsHeader } from './common-settings';

const passwordSchema = z.object({
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must include an uppercase letter')
    .regex(/[a-z]/, 'Password must include a lowercase letter')
    .regex(/[0-9]/, 'Password must include a number'),
});

export type PasswordFormValues = z.infer<typeof passwordSchema>;

interface PasswordUpdateFormProps {
  teamId: string;
}

export function PasswordUpdateForm({ teamId }: PasswordUpdateFormProps) {
  const form = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      password: '',
    },
    mode: 'onChange',
  });

  const updatePasswordMutation = useUpdateTeamPassword();

  const onSubmit = async (values: PasswordFormValues) => {
    updatePasswordMutation.mutate(
      { teamId, data: values },
      {
        onSuccess: () => {
          toast.success('Team password updated successfully!');
          form.reset();
        },
        onError: (error: any) => {
          if (error.data?.fields) {
            Object.entries(error.data.fields).forEach(([field, message]) => {
              form.setError(field as keyof PasswordFormValues, {
                type: 'server',
                message: message as string,
              });
            });
          }
        },
      },
    );
  };

  const { isValid, isDirty } = form.formState;

  return (
    <>
      <SettingsHeader
        title="Update Password"
        description="Change your team’s password here."
      />
      <Form {...form}>
        <SettingsFormWrapper
          onSubmit={form.handleSubmit(onSubmit)}
          isPending={updatePasswordMutation.isPending}
          isDirty={isDirty}
          isValid={isValid}
          submitLabel="Update Password"
        >
          <PasswordInput
            control={form.control}
            name="password"
            label="New Password"
          />
        </SettingsFormWrapper>
      </Form>
    </>
  );
}
